import { inject } from '@angular/core';
import { PlusAuthenticationService } from '@karve.it/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, concatMap, filter, map, mergeMap, of, switchMap, take, tap } from 'rxjs';

import { FreyaHelperService } from 'src/app/services/freya-helper.service';
import { PermissionService } from 'src/app/services/permission.service';
import { GetBlueSageTerminalsGQL } from 'src/generated/graphql.generated';

import { BlueSageTerminalPickerActions } from './bluesage-terminal-picker.actions';
import { blueSageTerminalPickerFeature } from './bluesage-terminal-picker.feature';
import { RECENT_TERMINALS_MAX, recentTerminalsKey } from './bluesage-terminal-picker.state';

/** Every action that should produce a fresh roster. */
const RELOADS = [
    BlueSageTerminalPickerActions.pickerOpened,
    BlueSageTerminalPickerActions.refreshButtonClicked,
    BlueSageTerminalPickerActions.terminalReleased,
] as const;

/**
 * Read the roster.
 *
 * `switchMap`, never `exhaustMap`: this is a READ, and both triggers have
 * already set `loading` in the reducer. `exhaustMap` DROPS a trigger that
 * arrives while a fetch is in flight, so no success or error action follows it
 * and the picker sits on a spinner over a stale roster — the exact defect the
 * review queue shipped with and had to fix in review. A refresh pressed twice
 * must show the second answer, not the first.
 *
 * `no-cache` because the whole point of the refresh button is to ask again. A
 * cached roster after someone powered a terminal on is worse than no roster.
 */
export const loadBlueSageTerminals = createEffect((
    actions$ = inject(Actions),
    getTerminalsGQL = inject(GetBlueSageTerminalsGQL),
    permissionHandler = inject(PermissionService),
) => {
    return actions$.pipe(
        ofType(...RELOADS),
        // Pair the trigger with the first permission emission; concatLatestFrom
        // drops the action when watchPermission hasn't emitted yet, which would
        // leave the terminal picker empty until the next reload.
        concatMap((action) => permissionHandler.watchPermission('bluesage.query').pipe(
            take(1),
            map((hasBluesageQuery) => [ action, hasBluesageQuery ] as const),
        )),
        filter(([, hasBluesageQuery]) => hasBluesageQuery),
        // `switchMap`, not `exhaustMap`: a refresh or release arriving while a
        // fetch is active must supersede it, or the roster stays stale until the
        // next trigger.
        switchMap(([ action ]) => of(action).pipe(
            mergeMap(() => getTerminalsGQL.fetch({}, { fetchPolicy: 'no-cache' }).pipe(
                map((res) => BlueSageTerminalPickerActions.loadTerminalsSuccess({
                    terminals: res.data.blueSageTerminals,
                })),
            )),
            catchError((error) => of(BlueSageTerminalPickerActions.loadTerminalsError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

/**
 * Restore this user's recent terminals when the picker opens.
 *
 * Deliberately never fails loudly. Recents are a shortcut, and a corrupt or
 * absent `localStorage` entry — a private window, a cleared profile, a value
 * some earlier version wrote — must render an ordinary picker rather than an
 * error on a payment screen.
 */
export const loadBlueSageRecentTerminals = createEffect((
    actions$ = inject(Actions),
    auth = inject(PlusAuthenticationService),
    freya = inject(FreyaHelperService),
) => {
    return actions$.pipe(
        ofType(BlueSageTerminalPickerActions.pickerOpened),
        map(() => BlueSageTerminalPickerActions.recentsLoaded({
            recentEpis: readRecentTerminals(auth.user?.id, freya.currentZone?.id),
        })),
    );
}, { functional: true, dispatch: true });

/**
 * Persist a terminal the operator actually SENT to.
 *
 * Reacting to the action rather than being called alongside a dispatch, per the
 * repo's services-and-actions convention — whichever surface pushed to a device
 * dispatches `terminalUsed` and nothing has to remember to also call a service.
 *
 * Recorded on USE, never on selection. A device someone merely clicked on is
 * not a device they work from, and letting selection write recents would fill
 * the shortcut with terminals nobody has ever taken a payment on.
 */
export const rememberBlueSageRecentTerminal = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    auth = inject(PlusAuthenticationService),
    freya = inject(FreyaHelperService),
) => {
    return actions$.pipe(
        ofType(BlueSageTerminalPickerActions.terminalUsed),
        // The reducer has already applied the ordering and the cap; write what it
        // decided rather than recomputing the same rule in two places.
        concatLatestFrom(() => store.select(blueSageTerminalPickerFeature.selectRecentEpis)),
        tap(([ , recentEpis ]) => {
            writeRecentTerminals(auth.user?.id, freya.currentZone?.id, recentEpis);
        }),
    );
}, { functional: true, dispatch: false });

function readRecentTerminals(userId?: string, zone?: string): string[] {
    if (!userId || !zone) { return []; }

    try {
        const raw = window.localStorage.getItem(recentTerminalsKey(userId, zone));
        const parsed: unknown = raw ? JSON.parse(raw) : [];

        return Array.isArray(parsed)
            ? parsed.filter((e): e is string => typeof e === 'string').slice(0, RECENT_TERMINALS_MAX)
            : [];
    } catch {
        return [];
    }
}

function writeRecentTerminals(userId: string | undefined, zone: string | undefined, epis: string[]): void {
    if (!userId || !zone) { return; }

    try {
        window.localStorage.setItem(recentTerminalsKey(userId, zone), JSON.stringify(epis));
    } catch {
        // A full or disabled localStorage must never break taking a payment.
    }
}
